import { create } from 'zustand'
import { dbStorage } from '../lib/indexedDBStorage'
import {
  mergeChapterDeviceThreadSuggestions,
  mergeWitnessDeviceSuggestions,
  reviewChapterDeviceThread,
  reviewWitnessDeviceOccurrence,
  type DeviceAnnotationMergeReport,
} from '../lib/deviceAnnotationLifecycle'
import type {
  ChapterDeviceLayer,
  DeviceAnnotationRunRecord,
  WitnessDeviceLayerRecord,
  ChapterDeviceThread,
  WitnessDeviceOccurrence,
  DeviceAnnotationStatus,
} from '../types/deviceAnnotationTypes'

interface DeviceAnnotationStoreState {
  chapterLayers: Map<string, ChapterDeviceLayer>
  witnessLayers: Map<string, WitnessDeviceLayerRecord>
  runs: Map<string, DeviceAnnotationRunRecord>

  getChapterDeviceLayer: (chapterId: string) => ChapterDeviceLayer | undefined
  loadChapterDeviceLayer: (chapterId: string) => Promise<ChapterDeviceLayer | undefined>
  saveChapterDeviceLayer: (layer: ChapterDeviceLayer) => Promise<void>

  getWitnessDeviceLayer: (witnessId: string) => WitnessDeviceLayerRecord | undefined
  loadWitnessDeviceLayer: (witnessId: string) => Promise<WitnessDeviceLayerRecord | undefined>
  saveWitnessDeviceLayer: (layer: WitnessDeviceLayerRecord) => Promise<void>
  mergeWitnessSuggestions: (params: {
    witnessId: string
    incomingOccurrences: WitnessDeviceOccurrence[]
    runId: string
    createdAt?: string
  }) => Promise<DeviceAnnotationMergeReport>
  reviewOccurrence: (params: {
    witnessId: string
    occurrenceId: string
    nextStatus: Extract<DeviceAnnotationStatus, 'confirmed' | 'edited' | 'rejected'>
    reviewer: string
    updates?: Parameters<typeof reviewWitnessDeviceOccurrence>[0]['updates']
    reviewedAt?: string
  }) => Promise<void>

  getRun: (runId: string) => DeviceAnnotationRunRecord | undefined
  loadRun: (runId: string) => Promise<DeviceAnnotationRunRecord | undefined>
  loadChapterRuns: (chapterId: string) => Promise<DeviceAnnotationRunRecord[]>
  saveRun: (run: DeviceAnnotationRunRecord) => Promise<void>
  mergeThreadSuggestions: (params: {
    chapterId: string
    incomingThreads: ChapterDeviceThread[]
    runId: string
    createdAt?: string
  }) => Promise<DeviceAnnotationMergeReport>
  reviewThread: (params: {
    chapterId: string
    threadId: string
    nextStatus: Extract<DeviceAnnotationStatus, 'confirmed' | 'edited' | 'rejected'>
    reviewer: string
    updates?: Parameters<typeof reviewChapterDeviceThread>[0]['updates']
    reviewedAt?: string
  }) => Promise<void>
}

export const useDeviceAnnotationStore = create<DeviceAnnotationStoreState>((set, get) => ({
  chapterLayers: new Map(),
  witnessLayers: new Map(),
  runs: new Map(),

  getChapterDeviceLayer: (chapterId) => get().chapterLayers.get(chapterId),
  loadChapterDeviceLayer: async (chapterId) => {
    const layer = await dbStorage.getChapterDeviceLayer(chapterId)
    if (layer) {
      set(state => ({
        chapterLayers: new Map(state.chapterLayers).set(chapterId, layer),
      }))
    }
    return layer
  },
  saveChapterDeviceLayer: async (layer) => {
    await dbStorage.saveChapterDeviceLayer(layer)
    set(state => ({
      chapterLayers: new Map(state.chapterLayers).set(layer.chapter_id, layer),
    }))
  },

  getWitnessDeviceLayer: (witnessId) => get().witnessLayers.get(witnessId),
  loadWitnessDeviceLayer: async (witnessId) => {
    const layer = await dbStorage.getWitnessDeviceLayer(witnessId)
    if (layer) {
      set(state => ({
        witnessLayers: new Map(state.witnessLayers).set(witnessId, layer),
      }))
    }
    return layer
  },
  saveWitnessDeviceLayer: async (layer) => {
    await dbStorage.saveWitnessDeviceLayer(layer)
    set(state => ({
      witnessLayers: new Map(state.witnessLayers).set(layer.witness_id, layer),
    }))
  },
  mergeWitnessSuggestions: async ({ witnessId, incomingOccurrences, runId, createdAt }) => {
    const existingLayer = get().witnessLayers.get(witnessId)
    if (!existingLayer) {
      throw new Error(`Cannot merge device annotation suggestions: witness layer ${witnessId} is not loaded.`)
    }
    const { layer, report } = mergeWitnessDeviceSuggestions({
      existingLayer,
      incomingOccurrences,
      runId,
      createdAt,
    })
    await dbStorage.saveWitnessDeviceLayer(layer)
    set(state => ({
      witnessLayers: new Map(state.witnessLayers).set(witnessId, layer),
    }))
    return report
  },
  reviewOccurrence: async ({ witnessId, occurrenceId, nextStatus, reviewer, updates, reviewedAt }) => {
    const existingLayer = get().witnessLayers.get(witnessId)
    if (!existingLayer) {
      throw new Error(`Cannot review device annotation occurrence: witness layer ${witnessId} is not loaded.`)
    }
    const layer = reviewWitnessDeviceOccurrence({
      layer: existingLayer,
      occurrenceId,
      nextStatus,
      reviewer,
      updates,
      reviewedAt,
    })
    await dbStorage.saveWitnessDeviceLayer(layer)
    set(state => ({
      witnessLayers: new Map(state.witnessLayers).set(witnessId, layer),
    }))
  },

  getRun: (runId) => get().runs.get(runId),
  loadRun: async (runId) => {
    const run = await dbStorage.getDeviceAnnotationRun(runId)
    if (run) {
      set(state => ({
        runs: new Map(state.runs).set(runId, run),
      }))
    }
    return run
  },
  loadChapterRuns: async (chapterId) => {
    const runs = await dbStorage.listDeviceAnnotationRunsForChapter(chapterId)
    if (runs.length > 0) {
      set(state => {
        const nextRuns = new Map(state.runs)
        runs.forEach(run => nextRuns.set(run.id, run))
        return { runs: nextRuns }
      })
    }
    return runs
  },
  saveRun: async (run) => {
    await dbStorage.saveDeviceAnnotationRun(run)
    set(state => ({
      runs: new Map(state.runs).set(run.id, run),
    }))
  },
  mergeThreadSuggestions: async ({ chapterId, incomingThreads, runId, createdAt }) => {
    const existingLayer = get().chapterLayers.get(chapterId)
    if (!existingLayer) {
      throw new Error(`Cannot merge device annotation thread suggestions: chapter layer ${chapterId} is not loaded.`)
    }
    const { layer, report } = mergeChapterDeviceThreadSuggestions({
      existingLayer,
      incomingThreads,
      runId,
      createdAt,
    })
    await dbStorage.saveChapterDeviceLayer(layer)
    set(state => ({
      chapterLayers: new Map(state.chapterLayers).set(chapterId, layer),
    }))
    return report
  },
  reviewThread: async ({ chapterId, threadId, nextStatus, reviewer, updates, reviewedAt }) => {
    const existingLayer = get().chapterLayers.get(chapterId)
    if (!existingLayer) {
      throw new Error(`Cannot review device annotation thread: chapter layer ${chapterId} is not loaded.`)
    }
    const layer = reviewChapterDeviceThread({
      layer: existingLayer,
      threadId,
      nextStatus,
      reviewer,
      updates,
      reviewedAt,
    })
    await dbStorage.saveChapterDeviceLayer(layer)
    set(state => ({
      chapterLayers: new Map(state.chapterLayers).set(chapterId, layer),
    }))
  },
}))
